import { dayStart } from '~/lib/dates'

/**
 * A day and what it read: the whole of what the trend chart needs to draw a point. What
 * produced the reading is the caller's business — an exercise's best set, a morning on the
 * scale — and the chart asks for none of it, so one chart serves both.
 */
export interface DayValue {
  /** The local calendar day, as `YYYY-MM-DD`. */
  readonly day: string
  readonly value: number
}

/**
 * The stretch of time a chart is drawn across, as a pair of moments. Given one, the width
 * means that stretch whether or not anything was logged at either end — which is how a chart
 * of the last seven days can show that the last three of them are empty.
 */
export interface Domain {
  readonly from: number
  readonly to: number
}

/**
 * Where each point sits across the width, as a fraction of it — scaled by time rather than
 * dealt evenly, so a fortnight's gap is drawn as a fortnight's gap.
 *
 * It lives out here because the chart is not the only thing that has to know: a crosshair
 * drawn over the chart has to land on the same place the curve did, and two answers to
 * "where is this day" is one answer too many.
 */
export function xFractions(points: readonly DayValue[], domain?: Domain): number[] {
  const times = points.map((point) => dayStart(point.day))
  const from = domain?.from ?? Math.min(...times)
  const span = Math.max(1, (domain?.to ?? Math.max(...times)) - from)
  return times.map((time) => (time - from) / span)
}

/**
 * How many whole days lie between two days. Counted by the calendar rather than by dividing
 * milliseconds: the night the clocks go back, two days running are twenty-five hours apart,
 * and an answer measured in hours would call them two days apart instead of one.
 */
export function daysApart(from: string, to: string): number {
  return Math.round((dayStart(to) - dayStart(from)) / 86_400_000)
}
